import { setWorkflowRestoreVersionDetails } from "./workflowRestoreVersionRepository";
import { Highlight } from "components/feature-update/FeatureUpdateModal";
import { FeatureLicenseType } from "domain/users";
import {
    ConnectedWorkflowsDto,
    LaunchType,
    QuotaData,
    ScheduleRequest,
    ScheduleResponse,
    ScheduleStatus,
    VersionsData,
    Workflow,
    WorkflowEntityType,
    WorkflowTableData,
    WorkflowVersionHistoryData,
} from "domain/workflows";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import { getLanguage } from "services/language/languageRepository";
import { getOliverUrl } from "services/login/endpointRepository";
import { WorkflowRestoreVersionDetails } from "store/workflow";
import { ParameterQuery } from "utils/commonFunctions";

export interface WorkflowDto {
    name: string;
    uuid: string;
    sequence_id: number;
    profile: string;
    description: string;
    created_date: string;
    user_uuid: string;
    user_name: string;
    default_workflow: DefaultWorkflow;
    workflow: Workflow;
    enabled: boolean;
    own: boolean;
    modified_date: string;
    modified_by_user_uuid: string;
    modified_by_username: string;
    editor_version: string;
    editor_generation: EditorGeneration;
    connected_workflows: ConnectedWorkflowsDto;
    secured: boolean;
    launch_source: LaunchSource;
    launch_type?: string;
    parameters?: Record<string, unknown>;
    schedule?: ScheduleStatus;
    asset_grading?: boolean;
}

export interface WorkflowVersionHistoryDto {
    workflow_uuid: string;
    editor_version: string;
    created_date: string;
    s3_version_id: string;
}

export interface QuotaDto {
    tenant_uuid: string;
    quota_type: QuotaType;
    total: number;
    used: number;
}

export interface DefaultWorkflow {
    own: boolean;
    other: OtherDefaultWorkflow;
}

export interface VersionsDto {
    versions: VersionsData[];
}

export interface DefaultWorkflowUser {
    username: string;
    name: string | null;
}

export interface OtherDefaultWorkflow {
    count: number;
    userDetails: DefaultWorkflowUser[];
}

export interface WorkflowVersionHistoryResponseDto {
    versions: WorkflowVersionHistoryDto[];
}

export interface WorkflowsResponseDto {
    workflows: WorkflowDto[];
    count: number;
    cursor: string;
}

export interface QuotasResponseDto {
    quotas: QuotaDto[];
}

export interface ManifestWorkflowEditorDto {
    version: string;
    profile: string;
    url: string;
    editor_generation: string;
    timestamp: string;
}

export interface ManifestWorkflowEditor {
    version: string;
    profile: string;
    url: string;
    editorGeneration: EditorGeneration;
    timestamp: string;
}

export function toManifestWorkflowEditor(dto: ManifestWorkflowEditorDto): ManifestWorkflowEditor {
    return {
        version: dto.version,
        profile: dto.profile,
        url: dto.url,
        editorGeneration: dto.editor_generation as EditorGeneration,
        timestamp: dto.timestamp,
    };
}

export interface WorkflowEditorsResponseDto {
    editors: ManifestWorkflowEditorDto[];
}

export interface WorkflowEditorV3SupportedEntry {
    profile: string;
    version: string;
}

export enum State {
    SUCCEEDED = "SUCCEEDED",
    FAILED = "FAILED",
    ALL = "ALL",
}

export enum QuickFilter {
    ALL = "ALL",
    CREATOR = "CREATOR",
}

export enum QuotaType {
    CONCURRENT_EXECUTIONS = "CONCURRENT_EXECUTIONS",
    CONDITION = "CONDITION",
    LOAD_REPORT = "LOAD_REPORT",
    SEARCH_REPORTS = "SEARCH_REPORTS",
    FOR_EACH = "FOR_EACH",
    SEND_EMAIL = "SEND_EMAIL",
    SERVER_MESSAGE = "SERVER_MESSAGE",
    SET_VALUE = "SET_VALUE",
    WORKFLOW_EXECUTIONS = "WORKFLOW_EXECUTIONS",
    SEARCH_ASSETS = "SEARCH_ASSETS",
    LOAD_ASSET = "LOAD_ASSET",
    SAVE_ASSET = "SAVE_ASSET",
    GRADE_ASSET = "GRADE_ASSET",
}

export enum FailureCode {
    USER_DEFINED = "USER_DEFINED",
    QUOTA_EXCEEDED = "QUOTA_EXCEEDED",
    UNKNOWN_ERROR = "UNKNOWN_ERROR",
    LICENSE_NOT_AVAILABLE = "LICENSE_NOT_AVAILABLE",
}

export interface Filter {
    state: State;
    test_mode: boolean;
    from: string | undefined;
    to: string | undefined;
    report_uuid: string;
}

export interface WorkflowStateDetails {
    state: State;
    started: string;
    completed: string;
    failureCode: FailureCode | null;
    reportUuid: string;
    testMode: boolean;
    contextId: string;
    parentContextId: string | null;
    hasSubWorkflowExecutions: boolean;
    hasSubWorkflowFailures: boolean;
    launchType: LaunchType;
    scheduled: boolean;
}

export interface WorkflowStateDetailsDto {
    state: State;
    started: string;
    completed: string;
    failure_code: FailureCode | null;
    report_uuid: string;
    test_mode: boolean;
    context_id: string;
    parent_context_id: string;
    has_subworkflow_executions: boolean;
    has_subworkflow_failures: boolean;
    launch_type: LaunchType;
    scheduled: boolean;
}

export interface SubworkflowExecutionStateDetails {
    state: State;
    contextId: string;
    failureCode: FailureCode | null;
    started: string;
    completed: string;
    workflowUuid: string;
    triggeringItemId: number;
    triggeringItemBriefText: string;
    workflowName: string;
}

export interface SubworkflowExecutionStateDetailsDto {
    state: State;
    context_id: string;
    failure_code: FailureCode | null;
    started: string;
    completed: string;
    workflow_uuid: string;
    triggering_item_id: number;
    triggering_item_brief_text: string;
    workflow_name: string;
}

export interface SubWorkflowTriggeringItems {
    itemId: number;
    itemBriefText: string;
}

export interface SubWorkflowTriggeringItemsDto {
    item_id: number;
    item_brief_text: string;
}

export interface SubworkflowExecutionStatesResponseDto {
    sub_workflow_triggering_items: SubWorkflowTriggeringItemsDto[];
    executions: SubworkflowExecutionStateDetailsDto[];
    count: number;
    cursor: string | null;
}

export interface WorkflowStatesResponseDto {
    workflow_state_details: WorkflowStateDetailsDto[];
    count: number;
    cursor: string;
}

export interface SuccessResponse {
    message: string;
}

export interface SaveSuccessResponse extends SuccessResponse {
    uuid: string;
    modified: string;
    modifiedBy: string;
    missingSubWorkflows: string[];
}

export interface SaveSuccessResponseDto {
    message: string;
    uuid: string;
    modified: string;
    modified_by: string;
    missing_sub_workflows?: string[];
}

export interface SetDefaultResponse extends SuccessResponse {
    oldDefaultUuid: string;
    oldDefaultName: string;
    newDefaultUuid: string;
    newDefaultName: string;
    profile: string;
}

export function toSubWorkflowTriggeringItems(dto: SubWorkflowTriggeringItemsDto): SubWorkflowTriggeringItems {
    return {
        itemId: dto.item_id,
        itemBriefText: dto.item_brief_text,
    };
}

export function toWorkflow(dto: WorkflowDto): WorkflowTableData {
    return {
        name: dto.name,
        uuid: dto.uuid,
        id: dto.sequence_id,
        product: dto.profile,
        profile: dto.profile.toLowerCase() as Profile,
        description: dto.description,
        createdDate: dto.created_date,
        modified: dto.modified_date,
        userUuid: dto.user_uuid,
        creator: dto.user_name,
        defaultWorkflow: dto.default_workflow,
        workflow: dto.workflow,
        enabled: dto.enabled,
        own: dto.own,
        editorVersion: dto.editor_version,
        editorGeneration: dto.editor_generation,
        connectedWorkflows: dto.connected_workflows
            ? {
                  parentWorkflows: dto.connected_workflows.parent_workflows,
                  subWorkflows: dto.connected_workflows.sub_workflows,
                  runDiagnoseWorkflows: dto.connected_workflows.run_diagnose_workflows,
                  runProcessFlowWorkflows: dto.connected_workflows.run_process_flow_workflows,
                  collectWorkflows: dto.connected_workflows.collect_workflows,
                  gradingPanelWorkflows: dto.connected_workflows.grading_panel_workflows,
                  forEachWorkflows: dto.connected_workflows.for_each_workflows,
                  advancedGradingWorkflows: dto.connected_workflows.advanced_grading_workflows,
                  remoteWorkflows: dto.connected_workflows.remote_workflow_count,
                  totalConnectedWorkflows:
                      dto.connected_workflows.parent_workflows.length +
                      dto.connected_workflows.sub_workflows.length +
                      dto.connected_workflows.run_diagnose_workflows.length +
                      dto.connected_workflows.run_process_flow_workflows.length +
                      dto.connected_workflows.collect_workflows.length +
                      dto.connected_workflows.grading_panel_workflows.length +
                      dto.connected_workflows.for_each_workflows.length +
                      dto.connected_workflows.advanced_grading_workflows.length +
                      dto.connected_workflows.remote_workflow_count,
              }
            : {
                  parentWorkflows: [],
                  subWorkflows: [],
                  runDiagnoseWorkflows: [],
                  runProcessFlowWorkflows: [],
                  collectWorkflows: [],
                  gradingPanelWorkflows: [],
                  forEachWorkflows: [],
                  advancedGradingWorkflows: [],
                  remoteWorkflows: 0,
                  totalConnectedWorkflows: 0,
              },
        secured: dto.secured,
        launchSource: dto.launch_source,
        launchType: dto.launch_type as LaunchType | undefined,
        parameters: dto.parameters,
        schedule: dto.schedule,
        assetGrading: dto.asset_grading,
    };
}

export function toWorkflowVersionHistory(dto: WorkflowVersionHistoryDto): WorkflowVersionHistoryData {
    return {
        workflowUuid: dto.workflow_uuid,
        editorVersion: dto.editor_version,
        createdDate: dto.created_date,
        s3VersionId: dto.s3_version_id,
    };
}

export function toQuotas(dto: QuotaDto): QuotaData {
    return {
        tenantUuid: dto.tenant_uuid,
        quotaType: dto.quota_type,
        total: dto.total,
        used: dto.used,
    };
}

export function toSaveSuccessResponse(dto: SaveSuccessResponseDto): SaveSuccessResponse {
    return {
        message: dto.message,
        uuid: dto.uuid,
        modified: dto.modified,
        modifiedBy: dto.modified_by,
        missingSubWorkflows: dto.missing_sub_workflows ?? [],
    };
}

export function toWorkflowStateDetails(dto: WorkflowStateDetailsDto): WorkflowStateDetails {
    return {
        state: dto.state,
        started: dto.started,
        completed: dto.completed,
        failureCode: dto.failure_code,
        reportUuid: dto.report_uuid,
        testMode: dto.test_mode,
        contextId: dto.context_id,
        parentContextId: dto.parent_context_id,
        hasSubWorkflowExecutions: dto.has_subworkflow_executions,
        hasSubWorkflowFailures: dto.has_subworkflow_failures,
        launchType: dto.launch_type,
        scheduled: dto.scheduled,
    };
}

export function toSubworkflowExecutionStateDetails(
    dto: SubworkflowExecutionStateDetailsDto
): SubworkflowExecutionStateDetails {
    return {
        state: dto.state,
        started: dto.started,
        completed: dto.completed,
        failureCode: dto.failure_code,
        contextId: dto.context_id,
        workflowUuid: dto.workflow_uuid,
        triggeringItemId: dto.triggering_item_id,
        triggeringItemBriefText: dto.triggering_item_brief_text,
        workflowName: dto.workflow_name,
    };
}

export enum Profile {
    ALL = "all",
    BDE = "bde",
    LUN = "lun",
    BMDE = "bmde",
    BMDE_DIAGNOSTIC = "bmde_diagnostic",
    BMP = "bmp",
}

export function isProfile(value: string): value is Profile {
    return (Object.values(Profile) as string[]).includes(value);
}

export enum LaunchSource {
    ALL = "ALL",
    API = "API",
    UI = "UI",
}

export type EditorGeneration = "V1" | "V2" | "V3" | null;
export const profileToNameMap = new Map([
    [Profile.BDE, "Drive Eraser"],
    [Profile.LUN, "LUN for Unix"],
    [Profile.BMDE, "Mobile Diagnostics and Erasure"],
    [Profile.BMDE_DIAGNOSTIC, "Mobile Diagnostics and Erasure - Diagnostics"],
    [Profile.BMP, "Management Portal"],
]);

export const profileToFeatureLicenseMap = new Map<Profile, FeatureLicenseType>([
    [Profile.BDE, "FEATURE_WORKFLOW_BDE"],
    [Profile.LUN, "FEATURE_WORKFLOW_LUN"],
    [Profile.BMDE, "FEATURE_WORKFLOW_BMDE"],
    [Profile.BMDE_DIAGNOSTIC, "FEATURE_WORKFLOW_BMDE"],
    [Profile.BMP, "FEATURE_WORKFLOW_BMP"],
]);

const PATH_API_WORKFLOWS = "/api/workflows";
const PATH_API_WORKFLOWS_V1 = "/api/v1/workflows";
const PATH_API_WORKFLOWS_V2 = "/api/v2/workflows";
const PATH_WORKFLOW_EDITORS = "/workflow-editors";
const PATH_API_WORKFLOW_QUOTAS_V1 = "/api/v1/workflow-quotas";

export function getReportViewUrl(reportUuid: string) {
    return `${getOliverUrl()}/api/reports/${reportUuid}?language=${getLanguage().code}&format=HTML`;
}
export class WorkflowService {
    public async deleteWorkflow(uuid: string, abortController: AbortController): Promise<void> {
        return apiGatewayService.invokeApi(`${PATH_API_WORKFLOWS}/${uuid}`, "DELETE", null, {
            abortSignal: abortController.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
        });
    }

    public async checkWorkflowNameAvailability(
        names: string[],
        abortController: AbortController
    ): Promise<Map<string, boolean>> {
        return apiGatewayService
            .invokeApi(
                PATH_API_WORKFLOWS_V2 + "/check-name-availability",
                "POST",
                { names: names },
                {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.OLIVER,
                    includeCredentials: true,
                }
            )
            .then((response: { availability: Record<string, boolean> }) => {
                return new Map(Object.entries(response.availability));
            });
    }

    public async fetchWorkflowEditors(
        profile: Profile,
        abortController?: AbortController
    ): Promise<ManifestWorkflowEditor[]> {
        return apiGatewayService
            .invokeApi(PATH_WORKFLOW_EDITORS + "/" + profile + "/manifest.json", "GET", null, {
                abortSignal: abortController?.signal,
                refreshSession: true,
                apiType: ApiType.STAN,
                includeCredentials: true,
            })
            .then((dto: WorkflowEditorsResponseDto) => dto.editors.map(toManifestWorkflowEditor));
    }

    public fetchWorkflowEditorV3Supported(
        abortController?: AbortController
    ): Promise<WorkflowEditorV3SupportedEntry[]> {
        const baseUrl = process.env.WORKFLOW_EDITOR_V3_URL ?? "";
        return fetch(`${baseUrl}/workflow-editors/V3/wfe-v3-manifest.json`, { signal: abortController?.signal })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch wfe-v3-manifest.json: ${response.status}`);
                }
                return response.json();
            })
            .then((data: { supported: WorkflowEditorV3SupportedEntry[] }) => data.supported);
    }

    public async fetchWorkflowEditorHighlights(
        profile: string,
        version: string,
        language: string
    ): Promise<Highlight[]> {
        return apiGatewayService.invokeApi(
            `${PATH_WORKFLOW_EDITORS}/${profile}/${version}/release_highlights/${language}.json`,
            "GET",
            null,
            {
                apiType: ApiType.BUCKET,
            }
        );
    }

    public async fetchWorkflows(
        search?: string,
        cursor?: string,
        product?: string,
        version?: string,
        from?: string,
        to?: string,
        userUuid?: string,
        abortController?: AbortController,
        status?: string
    ) {
        return apiGatewayService
            .invokeApi(
                PATH_API_WORKFLOWS_V1,
                "POST",
                {
                    search: search,
                    cursor: cursor,
                    product: product,
                    version: version,
                    from: from,
                    to: to,
                    user_uuid: userUuid,
                    status: status,
                },
                {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.OLIVER,
                    includeCredentials: true,
                }
            )
            .then((dto: WorkflowsResponseDto) => {
                return {
                    count: dto.count,
                    cursor: dto.cursor,
                    workflowTableData: dto.workflows.map(toWorkflow),
                };
            });
    }

    public async create(
        workflow: Workflow,
        subWorkflow?: boolean | undefined,
        abortController?: AbortController
    ): Promise<SaveSuccessResponse> {
        return apiGatewayService
            .invokeApi(
                PATH_API_WORKFLOWS,
                "POST",
                { workflow, subWorkflow },
                {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.OLIVER,
                    includeCredentials: true,
                }
            )
            .then((dto: SaveSuccessResponseDto) => toSaveSuccessResponse(dto));
    }

    public async launch(
        workflowUuid: string,
        parameters: Record<string, unknown>,
        abortController?: AbortController
    ): Promise<SaveSuccessResponse> {
        return apiGatewayService
            .invokeApi(
                `${PATH_API_WORKFLOWS_V1}/${workflowUuid}/launch`,
                "POST",
                { parameters: { ...parameters } },
                {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.OLIVER,
                    includeCredentials: true,
                }
            )
            .then((dto: SaveSuccessResponseDto) => toSaveSuccessResponse(dto));
    }

    public async update(
        uuid: string,
        workflow: Workflow,
        abortController?: AbortController
    ): Promise<SaveSuccessResponse> {
        return apiGatewayService
            .invokeApi(
                `${PATH_API_WORKFLOWS}/${uuid}`,
                "PUT",
                { workflow },
                {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.OLIVER,
                    includeCredentials: true,
                }
            )
            .then((dto: SaveSuccessResponseDto) => toSaveSuccessResponse(dto));
    }

    public async fetch(uuid: string, abortController?: AbortController): Promise<WorkflowDto> {
        return apiGatewayService.invokeApi(`${PATH_API_WORKFLOWS}/${uuid}`, "GET", null, {
            abortSignal: abortController?.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
        });
    }

    public async fetchQuotas(abortController?: AbortController): Promise<QuotasResponseDto> {
        return apiGatewayService.invokeApi(PATH_API_WORKFLOW_QUOTAS_V1, "GET", null, {
            abortSignal: abortController?.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
        });
    }

    public async export(uuid: string, encryption: boolean, abortController?: AbortController): Promise<void> {
        const query = new ParameterQuery().add("encryption", encryption ? "true" : "false").createQueryString();
        apiGatewayService.invokeApi(`${PATH_API_WORKFLOWS}/${uuid}/export${query}`, "GET", null, {
            abortSignal: abortController?.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
            setWindowLocation: true,
        });
    }

    public async fetchVersionList(uuid: string, abortController?: AbortController): Promise<VersionsDto> {
        return apiGatewayService.invokeApi(`${PATH_API_WORKFLOWS}/${uuid}/versions`, "GET", null, {
            abortSignal: abortController?.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
        });
    }

    public async fetchPreviouslySaveVersion(
        uuid: string,
        version: string,
        abortController?: AbortController
    ): Promise<WorkflowDto> {
        return apiGatewayService.invokeApi(`${PATH_API_WORKFLOWS}/${uuid}/versions/${version}`, "GET", null, {
            abortSignal: abortController?.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
        });
    }

    public async setDefault(uuid: string, abortController?: AbortController): Promise<SetDefaultResponse> {
        return apiGatewayService.invokeApi(`${PATH_API_WORKFLOWS}/${uuid}/default`, "POST", null, {
            abortSignal: abortController?.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
        });
    }

    public setActive(uuid: string, enable: boolean, abortController?: AbortController): Promise<void> {
        return apiGatewayService.invokeApi(
            `${PATH_API_WORKFLOWS_V1}/${uuid}/enable`,
            "POST",
            { enabled: enable },
            {
                abortSignal: abortController?.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
                emptyResponse: true,
            }
        );
    }

    public setSecure(uuid: string, secured: boolean, abortController?: AbortController): Promise<void> {
        return apiGatewayService.invokeApi(
            `${PATH_API_WORKFLOWS_V1}/${uuid}/secured`,
            "POST",
            { secured: secured },
            {
                abortSignal: abortController?.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
                emptyResponse: true,
            }
        );
    }

    public async fetchWorkflowStates(
        uuid: string,
        filter: Filter,
        cursor?: string | null,
        abortController?: AbortController
    ) {
        return apiGatewayService
            .invokeApi(
                `${PATH_API_WORKFLOWS_V1}/${uuid}/executions/state`,
                "POST",
                {
                    filter,
                    cursor,
                },
                {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.OLIVER,
                    includeCredentials: true,
                }
            )
            .then((dto: WorkflowStatesResponseDto) => {
                return {
                    workflowStateDetails: dto.workflow_state_details.map(toWorkflowStateDetails),
                    count: dto.count,
                    cursor: dto.cursor,
                };
            });
    }

    public async fetchSubworkflowExecutionStates(
        uuid: string,
        contextId: string,
        entityType: WorkflowEntityType,
        state: State,
        selectedItemId: string,
        cursor?: string | null,
        abortController?: AbortController
    ) {
        const requestBody: {
            state: State;
            cursor?: string | null;
            sub_workflow_triggering_item_id?: number;
            entity_type: WorkflowEntityType;
        } = {
            state: state,
            entity_type: entityType,
            cursor,
        };
        if (selectedItemId !== "") {
            requestBody.sub_workflow_triggering_item_id = Number(selectedItemId);
        }
        return apiGatewayService
            .invokeApi(
                `${PATH_API_WORKFLOWS_V1}/${uuid}/executions/${contextId}/subworkflow-executions`,
                "POST",
                requestBody,
                {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.OLIVER,
                    includeCredentials: true,
                }
            )
            .then((dto: SubworkflowExecutionStatesResponseDto) => {
                return {
                    subWorkflowTriggeringItems: dto.sub_workflow_triggering_items.map(toSubWorkflowTriggeringItems),
                    executions: dto.executions.map(toSubworkflowExecutionStateDetails),
                    count: dto.count,
                    cursor: dto.cursor,
                };
            });
    }

    public async retryFailedWorkflowExecution(
        workflowUuid: string,
        contextId: string,
        abortController?: AbortController
    ): Promise<{ contextId: string }> {
        return apiGatewayService.invokeApi(
            `${PATH_API_WORKFLOWS_V1}/${workflowUuid}/executions/${encodeURIComponent(contextId)}/retry`,
            "POST",
            null,
            {
                abortSignal: abortController?.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
            }
        );
    }

    public fetchExecutionLogs(
        workflowUuid: string,
        contextId: string,
        abortController?: AbortController
    ): Promise<{ contextId: string }> {
        return apiGatewayService.invokeApi(
            `${PATH_API_WORKFLOWS_V1}/${workflowUuid}/executions/${encodeURIComponent(contextId)}/execution-logs`,
            "GET",
            null,
            {
                abortSignal: abortController?.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
                setWindowLocation: true,
            }
        );
    }

    public fetchExecutionLogsContent(
        workflowUuid: string,
        contextId: string,
        abortController?: AbortController
    ): Promise<Record<string, unknown>> {
        return apiGatewayService.invokeApi(
            `${PATH_API_WORKFLOWS_V1}/${workflowUuid}/executions/${encodeURIComponent(contextId)}/execution-logs`,
            "GET",
            null,
            {
                abortSignal: abortController?.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
                setWindowLocation: false,
            }
        );
    }

    public async fetchWorkflowVersionHistory(
        uuid: string,
        abortController?: AbortController
    ): Promise<WorkflowVersionHistoryData[]> {
        return apiGatewayService
            .invokeApi(`${PATH_API_WORKFLOWS_V1}/${uuid}/versions/fetch`, "POST", null, {
                abortSignal: abortController?.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
            })
            .then((dto: WorkflowVersionHistoryResponseDto) => dto.versions.map(toWorkflowVersionHistory));
    }

    public async restoreWorkflow(uuid: string, versionId: string, abortController?: AbortController): Promise<void> {
        const query = new ParameterQuery().add("versionId", versionId).createQueryString();
        return apiGatewayService.invokeApi(`${PATH_API_WORKFLOWS_V1}/${uuid}/restore${query}`, "POST", null, {
            abortSignal: abortController?.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
            emptyResponse: true,
        });
    }

    public async createSchedule(
        workflowUuid: string,
        schedule: ScheduleRequest,
        abortController?: AbortController
    ): Promise<void> {
        return apiGatewayService.invokeApi(`${PATH_API_WORKFLOWS_V1}/${workflowUuid}/schedule`, "POST", schedule, {
            abortSignal: abortController?.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
            emptyResponse: true,
        });
    }

    public async fetchSchedule(workflowUuid: string, abortController?: AbortController): Promise<ScheduleResponse> {
        return apiGatewayService.invokeApi(`${PATH_API_WORKFLOWS_V1}/${workflowUuid}/schedule`, "GET", null, {
            abortSignal: abortController?.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
        });
    }

    public async fetchSchedules(
        workflowUuids: string[],
        abortController?: AbortController
    ): Promise<{ schedules: Record<string, ScheduleResponse> }> {
        return apiGatewayService.invokeApi(
            `${PATH_API_WORKFLOWS_V1}/schedules/fetch`,
            "POST",
            { workflow_uuids: workflowUuids },
            {
                abortSignal: abortController?.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
            }
        );
    }

    public async updateSchedule(
        workflowUuid: string,
        schedule: ScheduleRequest,
        abortController?: AbortController
    ): Promise<void> {
        return apiGatewayService.invokeApi(`${PATH_API_WORKFLOWS_V1}/${workflowUuid}/schedule`, "PUT", schedule, {
            abortSignal: abortController?.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
            emptyResponse: true,
        });
    }

    public async deleteSchedule(workflowUuid: string, abortController?: AbortController): Promise<void> {
        return apiGatewayService.invokeApi(`${PATH_API_WORKFLOWS_V1}/${workflowUuid}/schedule`, "DELETE", null, {
            abortSignal: abortController?.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
            emptyResponse: true,
        });
    }

    public storeRestoreWorkflowVersionDetails(details: WorkflowRestoreVersionDetails) {
        setWorkflowRestoreVersionDetails(details);
    }
}

export const workflowService = new WorkflowService();
